import { CheckCircle2, Mail, Plug, Plus, Save, Send, Trash2, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Page, PageHeader } from '../components/layout/AppShell';
import { ConfirmDialog, useToast } from '../components/ui/overlays';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  Input,
  LoadingBlock,
  Select,
  Switch,
  Textarea,
} from '../components/ui/primitives';
import {
  useDeleteMailbox,
  useMailboxes,
  useSaveMailbox,
  useSession,
  useTestMailbox,
} from '../hooks/queries';
import { formatRelative } from '../lib/utils';
import type { Mailbox, MailboxTestResult } from '../types/api';

const TIMEZONES = [
  'Europe/Brussels',
  'Europe/Amsterdam',
  'Europe/Berlin',
  'Europe/London',
  'Europe/Paris',
  'Europe/Madrid',
  'Europe/Oslo',
  'Europe/Warsaw',
  'America/New_York',
  'America/Los_Angeles',
  'UTC',
];

const DAYS = [
  ['1', 'Mon'],
  ['2', 'Tue'],
  ['3', 'Wed'],
  ['4', 'Thu'],
  ['5', 'Fri'],
  ['6', 'Sat'],
  ['7', 'Sun'],
] as const;

const BLANK = {
  label: 'Partnerships inbox',
  fromName: '',
  fromEmail: '',
  replyTo: '',
  smtpHost: '',
  smtpPort: 587,
  smtpSecure: false,
  smtpUser: '',
  smtpPassword: '',
  imapHost: '',
  imapPort: 993,
  imapSecure: true,
  imapUser: '',
  imapPassword: '',
  imapFolder: 'INBOX',
  saveToSent: false,
  sentFolder: '',
  signatureHtml: '',
  dailyLimit: 40,
  hourlyLimit: 10,
  minGapSeconds: 120,
  sendWindowStart: 8,
  sendWindowEnd: 18,
  sendDays: '1,2,3,4,5',
  timezone: 'Europe/Brussels',
  trackOpens: true,
  trackClicks: false,
  isDefault: true,
  isActive: true,
};

type Draft = typeof BLANK & { id?: string };

export const MailboxPage = () => {
  const { projectId = '' } = useParams<{ projectId: string }>();
  const { data: mailboxes, isLoading } = useMailboxes(projectId);
  const { data: session } = useSession();
  const saveMailbox = useSaveMailbox(projectId);
  const deleteMailbox = useDeleteMailbox(projectId);
  const testMailbox = useTestMailbox();
  const toast = useToast();

  const [draft, setDraft] = useState<Draft | null>(null);
  const [testResult, setTestResult] = useState<MailboxTestResult | null>(null);
  const [testAddress, setTestAddress] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Mailbox | null>(null);

  // Open the first mailbox for editing as soon as it loads: this page is almost always
  // "edit the one mailbox", not "browse a list".
  useEffect(() => {
    if (!mailboxes || draft) return;
    const first = mailboxes[0];
    if (first) setDraft(toDraft(first));
  }, [mailboxes, draft]);

  const save = async () => {
    if (!draft) return;
    try {
      const payload: Record<string, unknown> = { ...draft };
      // Empty password fields mean "keep what is stored".
      if (!draft.smtpPassword) delete payload.smtpPassword;
      if (!draft.imapPassword) delete payload.imapPassword;
      // An empty reply-to is "no reply-to", not the string "" — which fails email validation.
      if (!draft.replyTo) delete payload.replyTo;
      const saved = await saveMailbox.mutateAsync(payload as never);
      setDraft(toDraft(saved));
      toast.success('Mailbox saved');
    } catch (error) {
      toast.error(error);
    }
  };

  const test = async () => {
    if (!draft?.id) {
      toast.error('Save the mailbox first');
      return;
    }
    try {
      const result = await testMailbox.mutateAsync({
        id: draft.id,
        sendTestTo: testAddress || undefined,
      });
      setTestResult(result);
      if (result.smtp.ok) toast.success('SMTP connection works');
      else toast.error(`SMTP failed: ${result.smtp.error}`);
    } catch (error) {
      toast.error(error);
    }
  };

  if (isLoading) return <LoadingBlock />;

  return (
    <Page>
      <PageHeader
        title="Mailbox"
        description="The address this project sends from and reads replies on. SMTP for sending, IMAP for reading — a Gmail app password works for both."
        actions={
          <>
            {mailboxes && mailboxes.length > 1 ? (
              <Select
                className="w-auto"
                value={draft?.id ?? ''}
                onChange={(event) => {
                  const found = mailboxes.find((mailbox) => mailbox.id === event.target.value);
                  if (found) setDraft(toDraft(found));
                }}
              >
                {mailboxes.map((mailbox) => (
                  <option key={mailbox.id} value={mailbox.id}>
                    {mailbox.fromEmail}
                  </option>
                ))}
              </Select>
            ) : null}
            <Button icon={<Plus className="h-4 w-4" />} onClick={() => setDraft({ ...BLANK })}>
              Add mailbox
            </Button>
            {draft?.id ? (
              <Button
                icon={<Plug className="h-4 w-4" />}
                loading={testMailbox.isPending}
                onClick={test}
              >
                Test connection
              </Button>
            ) : null}
            <Button
              variant="primary"
              icon={<Save className="h-4 w-4" />}
              loading={saveMailbox.isPending}
              onClick={save}
              disabled={!draft}
            >
              Save
            </Button>
          </>
        }
      />

      {session?.warnings.emailDryRun ? (
        <div className="mb-4 rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-[13px] text-warning">
          <strong>Dry-run mode is on.</strong> Messages are rendered, logged and marked sent, but
          nothing reaches a real inbox. Set <code className="font-mono">EMAIL_DRY_RUN=false</code> in{' '}
          <code className="font-mono">packages/backend/.env</code> when you are ready. The
          &ldquo;send test email&rdquo; button below ignores dry-run mode on purpose.
        </div>
      ) : null}

      {!draft ? (
        <Card>
          <EmptyState
            icon={<Mail className="h-5 w-5" />}
            title="No mailbox configured"
            description="Add one to send outreach and read replies. Nothing can be sent until this exists."
            action={
              <Button variant="primary" onClick={() => setDraft({ ...BLANK })}>
                Add a mailbox
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            <Card>
              <CardHeader title="Identity" description="What recipients see" />
              <CardBody className="grid gap-4 sm:grid-cols-2">
                <Field label="Label" htmlFor="mb-label" hint="For your own reference.">
                  <Input
                    id="mb-label"
                    value={draft.label}
                    onChange={(event) => setDraft({ ...draft, label: event.target.value })}
                  />
                </Field>
                <Field label="From name" htmlFor="mb-fromname">
                  <Input
                    id="mb-fromname"
                    value={draft.fromName}
                    placeholder="Alex from RentX"
                    onChange={(event) => setDraft({ ...draft, fromName: event.target.value })}
                  />
                </Field>
                <Field label="From address" htmlFor="mb-fromemail">
                  <Input
                    id="mb-fromemail"
                    type="email"
                    value={draft.fromEmail}
                    placeholder="partnerships@rentx.example"
                    onChange={(event) => setDraft({ ...draft, fromEmail: event.target.value })}
                  />
                </Field>
                <Field label="Reply-to" htmlFor="mb-replyto" hint="Optional.">
                  <Input
                    id="mb-replyto"
                    type="email"
                    value={draft.replyTo}
                    onChange={(event) => setDraft({ ...draft, replyTo: event.target.value })}
                  />
                </Field>
                <Field
                  label="Signature"
                  htmlFor="mb-signature"
                  className="sm:col-span-2"
                  hint="Simple HTML. Appended to every message, so agents never write one."
                >
                  <Textarea
                    id="mb-signature"
                    rows={3}
                    className="prompt-input"
                    value={draft.signatureHtml}
                    placeholder='<div>Alex Ndoci<br />Partnerships, RentX<br /><a href="https://rentx.example">rentx.example</a></div>'
                    onChange={(event) => setDraft({ ...draft, signatureHtml: event.target.value })}
                  />
                </Field>
              </CardBody>
            </Card>

            <Card>
              <CardHeader
                title="SMTP — sending"
                description="Gmail: smtp.gmail.com:587 with an app password."
              />
              <CardBody className="grid gap-4 sm:grid-cols-2">
                <Field label="Host" htmlFor="mb-smtphost">
                  <Input
                    id="mb-smtphost"
                    value={draft.smtpHost}
                    placeholder="smtp.gmail.com"
                    onChange={(event) => setDraft({ ...draft, smtpHost: event.target.value })}
                  />
                </Field>
                <Field label="Port" htmlFor="mb-smtpport" hint="587 with STARTTLS, or 465 with TLS.">
                  <Input
                    id="mb-smtpport"
                    type="number"
                    value={draft.smtpPort}
                    onChange={(event) =>
                      setDraft({
                        ...draft,
                        smtpPort: Number(event.target.value) || 587,
                        smtpSecure: Number(event.target.value) === 465,
                      })
                    }
                  />
                </Field>
                <Field label="Username" htmlFor="mb-smtpuser">
                  <Input
                    id="mb-smtpuser"
                    value={draft.smtpUser}
                    onChange={(event) => setDraft({ ...draft, smtpUser: event.target.value })}
                  />
                </Field>
                <Field
                  label="Password"
                  htmlFor="mb-smtppass"
                  hint="Encrypted at rest and never returned. Leave empty to keep the stored one."
                >
                  <Input
                    id="mb-smtppass"
                    type="password"
                    value={draft.smtpPassword}
                    placeholder="••••••••"
                    onChange={(event) => setDraft({ ...draft, smtpPassword: event.target.value })}
                  />
                </Field>
                <div className="sm:col-span-2">
                  <Switch
                    checked={draft.smtpSecure}
                    onChange={(value) => setDraft({ ...draft, smtpSecure: value })}
                    label="Implicit TLS"
                    description="On for port 465, off for 587."
                  />
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader
                title="IMAP — reading replies"
                description="Without this, replies are never noticed and follow-ups keep going out."
              />
              <CardBody className="grid gap-4 sm:grid-cols-2">
                <Field label="Host" htmlFor="mb-imaphost">
                  <Input
                    id="mb-imaphost"
                    value={draft.imapHost}
                    placeholder="imap.gmail.com"
                    onChange={(event) => setDraft({ ...draft, imapHost: event.target.value })}
                  />
                </Field>
                <Field label="Port" htmlFor="mb-imapport">
                  <Input
                    id="mb-imapport"
                    type="number"
                    value={draft.imapPort}
                    onChange={(event) =>
                      setDraft({ ...draft, imapPort: Number(event.target.value) || 993 })
                    }
                  />
                </Field>
                <Field label="Username" htmlFor="mb-imapuser">
                  <Input
                    id="mb-imapuser"
                    value={draft.imapUser}
                    onChange={(event) => setDraft({ ...draft, imapUser: event.target.value })}
                  />
                </Field>
                <Field label="Password" htmlFor="mb-imappass" hint="Leave empty to keep the stored one.">
                  <Input
                    id="mb-imappass"
                    type="password"
                    value={draft.imapPassword}
                    placeholder="••••••••"
                    onChange={(event) => setDraft({ ...draft, imapPassword: event.target.value })}
                  />
                </Field>
                <Field label="Folder" htmlFor="mb-imapfolder">
                  <Input
                    id="mb-imapfolder"
                    value={draft.imapFolder}
                    onChange={(event) => setDraft({ ...draft, imapFolder: event.target.value })}
                  />
                </Field>

                <div className="border-t border-line pt-4 sm:col-span-2">
                  <Switch
                    checked={draft.saveToSent}
                    onChange={(value) => setDraft({ ...draft, saveToSent: value })}
                    label="Save a copy to the Sent folder"
                    description="Files each sent email into your Sent folder over IMAP, so it appears in your other mail client. Leave off for Gmail — it already saves sent mail, so a copy would be a duplicate."
                  />
                  {draft.saveToSent ? (
                    <div className="mt-3">
                      <Field
                        label="Sent folder"
                        htmlFor="mb-sentfolder"
                        hint="Leave empty to auto-detect (Sent, Sent Items, [Gmail]/Sent Mail…)."
                      >
                        <Input
                          id="mb-sentfolder"
                          value={draft.sentFolder}
                          placeholder="Auto-detect"
                          onChange={(event) => setDraft({ ...draft, sentFolder: event.target.value })}
                        />
                      </Field>
                    </div>
                  ) : null}
                </div>
              </CardBody>
            </Card>
          </div>

          <div className="space-y-4">
            <Card>
              <CardHeader title="Sending rules" description="What keeps the domain healthy" />
              <CardBody className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Per day" htmlFor="mb-daily">
                    <Input
                      id="mb-daily"
                      type="number"
                      min={0}
                      value={draft.dailyLimit}
                      onChange={(event) =>
                        setDraft({ ...draft, dailyLimit: Number(event.target.value) || 0 })
                      }
                    />
                  </Field>
                  <Field label="Per hour" htmlFor="mb-hourly">
                    <Input
                      id="mb-hourly"
                      type="number"
                      min={0}
                      value={draft.hourlyLimit}
                      onChange={(event) =>
                        setDraft({ ...draft, hourlyLimit: Number(event.target.value) || 0 })
                      }
                    />
                  </Field>
                </div>

                <Field
                  label="Minimum gap (seconds)"
                  htmlFor="mb-gap"
                  hint="A human does not send two emails in the same second."
                >
                  <Input
                    id="mb-gap"
                    type="number"
                    min={0}
                    value={draft.minGapSeconds}
                    onChange={(event) =>
                      setDraft({ ...draft, minGapSeconds: Number(event.target.value) || 0 })
                    }
                  />
                </Field>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Window from" htmlFor="mb-start">
                    <Select
                      id="mb-start"
                      value={String(draft.sendWindowStart)}
                      onChange={(event) =>
                        setDraft({ ...draft, sendWindowStart: Number(event.target.value) })
                      }
                    >
                      {Array.from({ length: 24 }, (_, hour) => (
                        <option key={hour} value={hour}>
                          {String(hour).padStart(2, '0')}:00
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Until" htmlFor="mb-end">
                    <Select
                      id="mb-end"
                      value={String(draft.sendWindowEnd)}
                      onChange={(event) =>
                        setDraft({ ...draft, sendWindowEnd: Number(event.target.value) })
                      }
                    >
                      {Array.from({ length: 24 }, (_, hour) => hour + 1).map((hour) => (
                        <option key={hour} value={hour}>
                          {String(hour).padStart(2, '0')}:00
                        </option>
                      ))}
                    </Select>
                  </Field>
                </div>

                <Field label="Timezone" htmlFor="mb-tz">
                  <Select
                    id="mb-tz"
                    value={draft.timezone}
                    onChange={(event) => setDraft({ ...draft, timezone: event.target.value })}
                  >
                    {TIMEZONES.map((zone) => (
                      <option key={zone} value={zone}>
                        {zone}
                      </option>
                    ))}
                  </Select>
                </Field>

                <div>
                  <span className="field-label">Send days</span>
                  <div className="flex flex-wrap gap-1">
                    {DAYS.map(([value, label]) => {
                      const active = draft.sendDays.split(',').includes(value);
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => {
                            const days = new Set(draft.sendDays.split(',').filter(Boolean));
                            active ? days.delete(value) : days.add(value);
                            setDraft({
                              ...draft,
                              sendDays: Array.from(days).sort().join(',') || '1',
                            });
                          }}
                          className={`rounded-md border px-2 py-1 text-xs font-medium transition-colors ${
                            active
                              ? 'border-brand bg-brand text-brand-ink'
                              : 'border-line bg-surface text-muted hover:text-ink'
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-3 border-t border-line pt-3">
                  <Switch
                    checked={draft.trackOpens}
                    onChange={(value) => setDraft({ ...draft, trackOpens: value })}
                    label="Track opens"
                    description="Adds a 1×1 pixel. Many clients block it."
                  />
                  <Switch
                    checked={draft.trackClicks}
                    onChange={(value) => setDraft({ ...draft, trackClicks: value })}
                    label="Track clicks"
                    description="Rewrites links through this server. Off by default — it hurts deliverability."
                  />
                  <Switch
                    checked={draft.isActive}
                    onChange={(value) => setDraft({ ...draft, isActive: value })}
                    label="Active"
                    description="Inactive mailboxes are skipped by the send and sync workers."
                  />
                  <Switch
                    checked={draft.isDefault}
                    onChange={(value) => setDraft({ ...draft, isDefault: value })}
                    label="Default for this project"
                  />
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Test" description="Verify the credentials actually work" />
              <CardBody className="space-y-3">
                <Field
                  label="Send a test email to"
                  htmlFor="mb-test"
                  hint="Optional. This one send ignores dry-run mode."
                >
                  <Input
                    id="mb-test"
                    type="email"
                    value={testAddress}
                    placeholder="you@example.com"
                    onChange={(event) => setTestAddress(event.target.value)}
                  />
                </Field>
                <Button
                  className="w-full"
                  icon={<Send className="h-4 w-4" />}
                  loading={testMailbox.isPending}
                  onClick={test}
                  disabled={!draft.id}
                >
                  {testAddress ? 'Test and send' : 'Test connection'}
                </Button>

                {testResult ? (
                  <div className="space-y-1.5 rounded-lg border border-line bg-raised px-3 py-2.5 text-xs">
                    <TestRow label="SMTP" ok={testResult.smtp.ok} error={testResult.smtp.error} />
                    <TestRow label="IMAP" ok={testResult.imap.ok} error={testResult.imap.error} />
                    {testResult.testSend ? (
                      <TestRow
                        label="Test send"
                        ok={testResult.testSend.ok}
                        error={testResult.testSend.error}
                      />
                    ) : null}
                  </div>
                ) : null}

                {draft.id ? (
                  <div className="space-y-1 border-t border-line pt-3 text-2xs text-faint">
                    {mailboxes
                      ?.filter((mailbox) => mailbox.id === draft.id)
                      .map((mailbox) => (
                        <div key={mailbox.id} className="space-y-1">
                          <div>
                            Last sync:{' '}
                            {mailbox.lastSyncAt ? formatRelative(mailbox.lastSyncAt) : 'never'}
                          </div>
                          {mailbox.lastSendError ? (
                            <div className="text-danger">Send error: {mailbox.lastSendError}</div>
                          ) : null}
                          {mailbox.lastSyncError ? (
                            <div className="text-danger">Sync error: {mailbox.lastSyncError}</div>
                          ) : null}
                          <div className="flex gap-1.5 pt-1">
                            {mailbox.hasSmtpPassword ? (
                              <Badge tone="positive">SMTP password set</Badge>
                            ) : (
                              <Badge tone="warning">no SMTP password</Badge>
                            )}
                            {mailbox.hasImapPassword ? (
                              <Badge tone="positive">IMAP password set</Badge>
                            ) : (
                              <Badge tone="warning">no IMAP password</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                ) : null}

                {draft.id ? (
                  <Button
                    variant="ghost"
                    className="w-full"
                    icon={<Trash2 className="h-4 w-4" />}
                    onClick={() => {
                      const found = mailboxes?.find((mailbox) => mailbox.id === draft.id);
                      if (found) setDeleteTarget(found);
                    }}
                  >
                    Delete mailbox
                  </Button>
                ) : null}
              </CardBody>
            </Card>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title="Delete mailbox"
        destructive
        confirmLabel="Delete"
        loading={deleteMailbox.isPending}
        message={
          <>
            Delete <strong className="text-ink">{deleteTarget?.fromEmail}</strong>? Emails sent from
            it are removed too.
          </>
        }
        onConfirm={async () => {
          if (!deleteTarget) return;
          try {
            await deleteMailbox.mutateAsync(deleteTarget.id);
            toast.success('Mailbox deleted');
            setDeleteTarget(null);
            setDraft(null);
          } catch (error) {
            toast.error(error);
          }
        }}
      />
    </Page>
  );
};

const TestRow = ({ label, ok, error }: { label: string; ok: boolean; error?: string }) => (
  <div className="flex items-start gap-2">
    {ok ? (
      <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-positive" />
    ) : (
      <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-danger" />
    )}
    <span className="min-w-0">
      <span className="font-medium text-ink">{label}</span>
      {error ? <span className="ml-1 text-muted">— {error}</span> : null}
    </span>
  </div>
);

const toDraft = (mailbox: Mailbox): Draft => ({
  id: mailbox.id,
  label: mailbox.label,
  fromName: mailbox.fromName,
  fromEmail: mailbox.fromEmail,
  replyTo: mailbox.replyTo ?? '',
  smtpHost: mailbox.smtpHost,
  smtpPort: mailbox.smtpPort,
  smtpSecure: mailbox.smtpSecure,
  smtpUser: mailbox.smtpUser,
  smtpPassword: '',
  imapHost: mailbox.imapHost ?? '',
  imapPort: mailbox.imapPort,
  imapSecure: mailbox.imapSecure,
  imapUser: mailbox.imapUser ?? '',
  imapPassword: '',
  imapFolder: mailbox.imapFolder,
  saveToSent: mailbox.saveToSent,
  sentFolder: mailbox.sentFolder ?? '',
  signatureHtml: mailbox.signatureHtml ?? '',
  dailyLimit: mailbox.dailyLimit,
  hourlyLimit: mailbox.hourlyLimit,
  minGapSeconds: mailbox.minGapSeconds,
  sendWindowStart: mailbox.sendWindowStart,
  sendWindowEnd: mailbox.sendWindowEnd,
  sendDays: mailbox.sendDays,
  timezone: mailbox.timezone,
  trackOpens: mailbox.trackOpens,
  trackClicks: mailbox.trackClicks,
  isDefault: mailbox.isDefault,
  isActive: mailbox.isActive,
});
